import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { IAuthContext, IAuthContextProvider } from '../interfaces/IAuthContext';
import { ISignon, initialSignon } from '../interfaces/ISignon';
import { useCookies } from './CookieContext';
import { Skeleton } from 'antd';
import LoadingSpinner from '../components/LoadingSpinner';

// Crear el contexto de autenticación con valores por defecto.
// Este contexto proporcionará el estado de autenticación, las funciones de login/logout y los datos del usuario.
const AuthContext = createContext<IAuthContext>({
    isAuthenticated: false,
    login: (login: ISignon) => { },
    logout: () => { },
    data: initialSignon
});

// Hook personalizado para acceder al contexto de autenticación.
// Facilita el uso del contexto en otros componentes.
export function useAuth() {
    return useContext(AuthContext);
}

// Proveedor del contexto de autenticación.
// Este componente envuelve a la aplicación (o a una parte de ella) y proporciona el contexto a sus hijos.
export function AuthContextProvider({ children }: IAuthContextProvider) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [data, setData] = useState<ISignon>(initialSignon);
    const { getCookie, removeCookie, setCookie } = useCookies();
    const [isLoading, setIsLoading] = useState<boolean>(true); // Estado de carga inicial.

    // Efecto para verificar si el usuario ya está autenticado al cargar el componente.
    useEffect(() => {
        try {
            // obtener la cookie del usuario
            const cookieUser = getCookie('user');
            // Si existe la cookie del usuario, significa que el usuario ya inició sesión.
            if (cookieUser) {
                // Obtener los permisos del usuario desde localStorage.
                const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
                // Actualizar el estado con los datos del usuario y sus permisos.
                setData({ ...cookieUser as object, permissions } as ISignon);
                setIsAuthenticated(true); // Marcar como autenticado
            }
        } catch (error) {
            // Si hay un error al recuperar la cookie, limpiar la cookie y los permisos.
            console.error("Error al recuperar la cookie de usuario:", error);
            removeCookie('user');
            localStorage.removeItem('permissions');
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 1000);
        }
    }, []);

    // Función para iniciar sesión.
    const login = (login: ISignon) => {
        // Separar los permisos del resto de los datos del usuario.
        const { permissions, ...loginWithoutPermissions } = login;
        // Actualizar el estado con los datos del usuario.
        setData(login);
        setIsAuthenticated(true);
        // Guardar los datos del usuario (sin permisos) en una cookie.
        setCookie('user', loginWithoutPermissions);
        // Guardar los permisos en localStorage (ya que son demasiado grandes para una cookie).
        localStorage.setItem('permissions', JSON.stringify(permissions));
    };

    // Función para cerrar sesión.
    const logout = () => {
        // Eliminar la cookie del usuario.
        removeCookie('user');
        // Marcar como no autenticado.
        setIsAuthenticated(false);
        // Restablecer los datos del usuario a su estado inicial.
        setData(initialSignon);
        // Eliminar los permisos del localStorage
        localStorage.removeItem('permissions');
    };

    /**
     * Utilizamos useMemo para memorizar el objeto de contexto 
     * que se pasa al proveedor de contexto (AuthContext.Provider), para evitar que 
     * se vuelva a renderizar el proveedor y todos sus componentes hijos cada vez que 
     * cambie el estado de autenticación.
     */
    const authContextValue = useMemo(() => {
        return { isAuthenticated, login, logout, data, isLoading };
    }, [isAuthenticated, data, login, logout, isLoading]);

    // Proveer el contexto a los componentes hijos.
    return (
        <AuthContext.Provider value={authContextValue}>
            {isLoading ? <LoadingSpinner /> : children}
        </AuthContext.Provider>
    );
};
